/**
 * Admin Extra Events API Client
 * ==============================
 * Functions for admin-only extra event management.
 */

const BACKEND_URL = window.location.origin;

/**
 * Create a new extra event.
 * @param {Object} eventData
 * @param {string} token - Auth token
 * @returns {Promise<Object>} Created event with warnings
 */
export async function createExtraEvent(eventData, token) {
  const response = await fetch(`${BACKEND_URL}/api/admin/events/extra`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(eventData),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail || "Failed to create event");
  }

  return response.json();
}

/**
 * List extra events.
 * @param {Object} options
 * @param {string} options.range - "today" or "week"
 * @param {string} token - Auth token
 * @returns {Promise<Object>} Events list with counts
 */
export async function listExtraEvents({ range = "today" } = {}, token) {
  const params = new URLSearchParams();
  params.append("range", range);

  const response = await fetch(
    `${BACKEND_URL}/api/admin/events/extra?${params.toString()}`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail || "Failed to list events");
  }

  return response.json();
}

/**
 * Get a single extra event.
 * @param {string} eventId
 * @param {string} token - Auth token
 * @returns {Promise<Object>} Event details
 */
export async function getExtraEvent(eventId, token) {
  const response = await fetch(
    `${BACKEND_URL}/api/admin/events/extra/${eventId}`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail || "Event not found");
  }

  return response.json();
}

/**
 * Update an extra event.
 * @param {string} eventId
 * @param {Object} updates
 * @param {string} token - Auth token
 * @returns {Promise<Object>} Updated event with warnings
 */
export async function updateExtraEvent(eventId, updates, token) {
  const response = await fetch(
    `${BACKEND_URL}/api/admin/events/extra/${eventId}`,
    {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(updates),
    }
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail || "Failed to update event");
  }

  return response.json();
}

/**
 * Cancel an extra event.
 * @param {string} eventId
 * @param {string} token - Auth token
 * @returns {Promise<Object>} Cancel result
 */
export async function cancelExtraEvent(eventId, token) {
  const response = await fetch(
    `${BACKEND_URL}/api/admin/events/extra/${eventId}/cancel`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail || "Failed to cancel event");
  }

  return response.json();
}

export default {
  createExtraEvent,
  listExtraEvents,
  getExtraEvent,
  updateExtraEvent,
  cancelExtraEvent,
};
